import { useContext, useMemo, useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  CapacityStateWithContext,
  FormStatus,
} from "@thalamos/aspire-common";
import {
  BID_TEMPLATE_ID,
  canManageCareHomeRegister,
  canViewCareHomeRegister,
  CapacityDecisionType,
  CapacityOutcome,
  capacityStatusPills,
  classifyCapacityExpiry,
  DeprivationOutcome,
  FDMCA_TEMPLATE_ID,
  formIsCompleted,
  formIsFdmca,
  liveMcaForms,
  mcaContextPrompt,
} from "@thalamos/aspire-common";

import { FormContextPdfViewer } from "../FormProgressPage/helpers/FormContextPdfViewer.js";
import { NewFormDialog } from "../NewFormDialog.js";
import { RecordCapacityStatusDialog } from "./RecordCapacityStatusDialog.js";
import { RemoveResidentDialog } from "./RemoveResidentDialog.js";
import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";
import { ukLocalFormatDate } from "~/util.js";

/**
 * The three assessments that make up a capacity episode. Each can be recorded by hand or
 * completed as a form — except the deprivation of liberty test, which has no template
 * yet, so its outcome can only be asserted until one exists.
 */
const pathway = [
  {
    key: "mca",
    title: "Mental Capacity Assessment",
    stripe: "#2857A4",
    templateId: FDMCA_TEMPLATE_ID as string | undefined,
  },
  {
    key: "dol",
    title: "Deprivation of Liberty Test (Multifactorial Assessment)",
    stripe: "#8A63D2",
    templateId: undefined,
  },
  {
    key: "bid",
    title: "Best Interest Decision",
    stripe: "#008758",
    templateId: BID_TEMPLATE_ID as string | undefined,
  },
] as const;

type PathwayFormState =
  | { kind: "none" }
  | { kind: "in-progress"; formId: string }
  | { kind: "complete" }
  | { kind: "invited" }
  | { kind: "not-required" };

/**
 * What a pathway row is asking for, read off the residence assessment rather than off an
 * open context: the invitation is derived, so it persists for as long as it is owed.
 *
 * An FDMCA that found capacity closes the episode — a best interest decision does not
 * follow from it — while one that found a lack of capacity leaves a BID outstanding. That
 * distinction is #4309's (`mcaContextPrompt`), and it is the difference between a row
 * that is finished and one that is merely empty.
 */
function pathwayFormState(
  templateId: string | undefined,
  forms: McaFormWithStatus[],
  invitedTemplateId: string | undefined,
): PathwayFormState {
  if (!templateId) return { kind: "none" };

  const form = forms.find((f) => f.formTemplate.id === templateId);

  if (form) {
    return formIsCompleted(form)
      ? { kind: "complete" }
      : { kind: "in-progress", formId: form.id };
  }

  if (invitedTemplateId === templateId) return { kind: "invited" };

  // A BID is not owed by somebody who has capacity for the decision, so the row says so
  // rather than sitting empty and implying something outstanding.
  if (
    templateId === BID_TEMPLATE_ID &&
    forms.some((f) => formIsFdmca(f) && formIsCompleted(f)) &&
    invitedTemplateId !== BID_TEMPLATE_ID
  ) {
    return { kind: "not-required" };
  }

  return { kind: "none" };
}

type McaFormWithStatus = {
  id: string;
  formTemplate: { id: string; version: string };
  status: FormStatus;
};

const statusPillColours: Record<
  string,
  { background: string; colour: string }
> = {
  "Has capacity": { background: "#FBF2D6", colour: "#7A5E00" },
  "Lacks capacity": { background: "#FBF2D6", colour: "#7A5E00" },
  Consenting: { background: "#FBF2D6", colour: "#7A5E00" },
  "Restriction identified": { background: "#FBE9E2", colour: "#962F0D" },
  "Consenting with capacity": { background: "#E4F2EC", colour: "#00674A" },
  "Lacks capacity, DoLS applies": { background: "#E8EFF9", colour: "#1F4785" },
  "Lacking capacity, but validly consenting": {
    background: "#EEF0F3",
    colour: "#4B5063",
  },
  "Safeguarding concern": { background: "#FBE9E2", colour: "#962F0D" },
};

function StatusPill({ label }: { label: string }) {
  const { background, colour } = statusPillColours[label] ?? {
    background: "#F2F3F5",
    colour: "#6C7186",
  };

  return (
    <Box
      component="span"
      data-testid="resident-status-pill"
      sx={{
        display: "inline-block",
        backgroundColor: background,
        color: colour,
        borderRadius: "11px",
        padding: "3px 10px",
        fontSize: "0.78rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One entry in the resident's assessment history. The RECORD / FORM tag is the whole
 * point of the row: it says whether a position was assessed or asserted, which is a
 * question a reader of a legal record has to be able to answer.
 */
function HistoryEntry({
  state,
  patientId,
  onViewPdf,
}: {
  state: CapacityStateWithContext;
  patientId: string;
  onViewPdf: (formId: string) => void;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isForm = !!state.formId;
  const outcome =
    state.state.capacity.outcome === CapacityOutcome.HasCapacity
      ? "Has capacity"
      : state.state.capacity.outcome === CapacityOutcome.LacksCapacity
        ? "Lacks capacity"
        : "Capacity not assessed";

  const deprivation =
    state.state.deprivation.outcome === DeprivationOutcome.Deprived
      ? "A deprivation of liberty"
      : state.state.deprivation.outcome === DeprivationOutcome.NotDeprived
        ? "Not a deprivation of liberty"
        : undefined;

  const meta = [
    state.recordedByName,
    ukLocalFormatDate(state.effectiveDateTime),
    state.state.dols?.expiryDateTime
      ? `Expires ${ukLocalFormatDate(state.state.dols.expiryDateTime)}`
      : undefined,
  ].filter(Boolean);

  return (
    <Box
      data-testid="history-entry"
      sx={{
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        paddingLeft: "0.9em",
        marginBottom: "1.2em",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Box
          component="span"
          sx={{
            backgroundColor: theme.palette.common.paleBlue,
            color: theme.palette.primary.main,
            fontSize: "0.62rem",
            letterSpacing: "0.08em",
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: "3px",
          }}
        >
          MCA
        </Box>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
          {isForm ? "Mental Capacity Assessment" : "Status recorded"}
        </Typography>
        <Box
          component="span"
          sx={{
            backgroundColor: "#EEF0F3",
            color: "#4B5063",
            fontSize: "0.6rem",
            letterSpacing: "0.08em",
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: "3px",
          }}
        >
          {isForm ? "FORM" : "RECORD"}
        </Box>
      </Box>

      <Typography sx={{ fontSize: "0.95rem", color: "#7B7D89" }}>
        {outcome}
      </Typography>
      {deprivation && (
        <Typography sx={{ fontSize: "0.8rem", color: "#7B7D89" }}>
          {deprivation}
        </Typography>
      )}
      <Typography
        sx={{ fontSize: "0.78rem", color: "#54596B", fontStyle: "italic" }}
      >
        {state.state.decision.type === CapacityDecisionType.Residence
          ? "Decision: Reside in the care home"
          : `Decision: ${state.state.decision.description}`}
      </Typography>
      {meta.length > 0 && (
        <Typography sx={{ fontSize: "0.75rem", color: "#9A9CA1", mt: 0.3 }}>
          {meta.join("  ·  ")}
        </Typography>
      )}
      {(state.formId || state.formContextId) && (
        // The document is the evidence for the entry above it, so it is reachable from
        // the point in the history it belongs to rather than only from the pathway.
        <Box sx={{ display: "flex", gap: 1, mt: 0.8, flexWrap: "wrap" }}>
          {state.formId && (
            <Button
              label="View PDF"
              variant="outlined"
              size="small"
              onClick={() => {
                onViewPdf(state.formId!);
              }}
            />
          )}
          {state.formContextId && (
            <Button
              label="Open assessment"
              variant="outlined"
              size="small"
              onClick={() => {
                navigate(
                  routeFns.formContextPage(state.formContextId!, patientId),
                );
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export function ResidentPage(_props: Record<string, never>) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { patientId } = useParams();
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const { managedLocations } = useContext(ManagedLocationsContext);

  const [pdfViewFormId, setPdfViewFormId] = useState<string | null>(null);
  const [showRecord, setShowRecord] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [careHomeToAdd, setCareHomeToAdd] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const [{ data: register }] = apiHooks.capacityState.register();

  const [
    { data: historyResponse, loading, error: historyError },
    refetchHistory,
  ] = apiHooks.capacityState.history(patientId!);

  // A refusal arrives as an error body in place of the list, so the shape must be checked
  // before it is read: a resident this team may not see has to say so, not crash.
  const history = Array.isArray(historyResponse) ? historyResponse : undefined;
  const isInaccessible = !!historyError || (!loading && !history);
  const [{ data: patient }] = apiHooks.patients.get(patientId!);
  const [{ data: timeline }, refetchTimeline] = apiHooks.patients.getTimeline(
    patientId!,
  );

  // Only the residence assessment drives this pathway: a resident can also have been
  // assessed for a different decision, and that episode owes nothing here.
  const residenceAssessment = useMemo(() => {
    const contexts = [
      ...(timeline?.inProgressWork?.items ?? []),
      ...(timeline?.completedWork?.items ?? []),
    ];
    return contexts.find(
      (context) =>
        context.type === "mca-assessment" &&
        context.capacityDecision?.type === "residence",
    );
  }, [timeline]);

  const assessmentForms = useMemo(
    () => liveMcaForms(residenceAssessment?.forms ?? []),
    [residenceAssessment],
  );

  const invitedTemplateId = useMemo(
    () => mcaContextPrompt(assessmentForms)?.templateId,
    [assessmentForms],
  );

  const inProgressForms = useMemo(
    () => assessmentForms.filter((form) => !formIsCompleted(form)),
    [assessmentForms],
  );

  const canManage = !!canManageCareHomeRegister(user);

  // Sight of a home carries no authority to write to it, and the two are granted per
  // home, so every action here is gated on the home in question rather than on the
  // session alone.
  const addableHomes = (register?.careHomes ?? []).filter(
    (home) => home.canManageRegister,
  );
  const canAddToARegister = canManage && addableHomes.length > 0;

  const current = useMemo(
    () => history?.find((state) => state.isLatest),
    [history],
  );

  const careHomeName = managedLocations.find(
    (location) => location.id === current?.state.residency?.careHomeId,
  )?.name;

  // Whether this resident in particular may be written to: recording a status, correcting
  // one and ending a residency all assert something about the home they live in.
  const canManageThisResident =
    canManage &&
    addableHomes.some(
      (home) => home.id === current?.state.residency?.careHomeId,
    );

  const [formTemplateId, setFormTemplateId] = useState<string | null>(null);

  if (!canViewCareHomeRegister(user)) {
    return (
      <Banner
        bannerType={BannerList.ERROR}
        title="You do not have access to care home registers"
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (isInaccessible) {
    return (
      <Box sx={{ padding: "1em 1.5em" }}>
        <Banner
          bannerType={BannerList.ERROR}
          title="This person is not on a register you can see. Find them through patient search to add them."
        />
      </Box>
    );
  }

  const pills = current ? capacityStatusPills(current.state) : ["No Status"];

  // A repeat assessment is usually a renewal rather than a first look, and which one it is
  // is knowable from the clock: naming it tells the assessor what the form is for.
  const isRenewal =
    !!current &&
    ["expired", "critical"].includes(
      classifyCapacityExpiry(
        current.state,
        dayjs(),
        user?.sessionOrganisationConfiguration?.mcaCapacityReviewIntervalMonths,
      ),
    );

  // Capacity settles only half the question. Whether living here deprives somebody of
  // their liberty is a separate finding, and until it is made the status can say nothing
  // about consent or DoLS — so the page asks for it rather than waiting to be asked.
  const consentQuestionOutstanding =
    !!current &&
    !!current.state.residency &&
    !current.state.residency.endDateTime &&
    current.state.capacity.outcome !== CapacityOutcome.NotAssessed &&
    current.state.deprivation.outcome === DeprivationOutcome.NotAssessed;

  return (
    <Box sx={{ padding: "1em 1.5em" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
          gap: 2,
        }}
      >
        <Link
          to={routeFns.careHomeRegister()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4em",
            color: theme.palette.primary.main,
            textDecoration: "none",
            fontSize: "0.9rem",
          }}
        >
          <ArrowBack sx={{ fontSize: "1rem" }} />
          Back to resident list
        </Link>

        {canManageThisResident && current?.state.residency && (
          <Button
            label="Remove resident"
            color="error"
            variant="outlined"
            startIcon={ButtonIcon.delete}
            onClick={() => {
              setShowRemove(true);
            }}
          />
        )}
      </Box>

      <Paper variant="outlined" sx={{ padding: "1.2em 1.4em", mb: 2 }}>
        <Box
          sx={{
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            paddingLeft: "0.8em",
          }}
        >
          <Typography
            component="h1"
            sx={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.2 }}
          >
            {patient
              ? `${patient.name.given} ${patient.name.family}`
              : "Resident"}
          </Typography>
          <Typography
            sx={{ fontSize: "0.85rem", color: "#54596B", marginTop: "0.3em" }}
          >
            {[
              `NHS ${NhsNumberSchema.catch("unknown").parse(patient?.nhsNumber)}`,
              patient?.dateOfBirth
                ? `DOB: ${ukLocalFormatDate(patient.dateOfBirth)} (age ${String(dayjs().diff(dayjs(patient.dateOfBirth), "years"))})`
                : "DOB unknown",
              careHomeName,
            ]
              .filter(Boolean)
              .join("      ")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            marginTop: "1.2em",
          }}
        >
          {pills.map((pill) => (
            <StatusPill key={pill} label={pill} />
          ))}
          {canManageThisResident && (
            <Button
              label="Edit"
              variant="outlined"
              size="small"
              startIcon={ButtonIcon.edit}
              onClick={() => {
                setShowRecord(true);
              }}
            />
          )}
        </Box>

        {current && (
          <Typography sx={{ fontSize: "0.8rem", color: "#7B7D89", mt: 0.8 }}>
            {`Since ${ukLocalFormatDate(current.effectiveDateTime)}`}
          </Typography>
        )}
        <Typography
          sx={{ fontSize: "0.8rem", color: "#54596B", fontStyle: "italic" }}
        >
          {careHomeName
            ? `Regarding: decision to reside at ${careHomeName}`
            : "Regarding: decision to reside"}
        </Typography>

        {consentQuestionOutstanding && (
          <Box
            data-testid="consent-outstanding"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              backgroundColor: "#FBF2D6",
              borderRadius: "4px",
              padding: "0.7em 0.9em",
              mt: 1.2,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", color: "#7A5E00" }}>
              {current?.state.capacity.outcome === CapacityOutcome.HasCapacity
                ? "Capacity for this decision has been assessed. Record whether they are consenting to reside here, or whether the arrangements deprive them of their liberty."
                : "Capacity for this decision has been assessed. Record whether the arrangements deprive them of their liberty, which decides whether DoLS applies."}
            </Typography>
            {canManageThisResident && (
              <Button
                label="Record consent status"
                size="small"
                startIcon={ButtonIcon.create}
                onClick={() => {
                  setShowRecord(true);
                }}
              />
            )}
          </Box>
        )}
      </Paper>

      {!current?.state.residency && (
        <Paper
          variant="outlined"
          data-testid="not-on-a-register"
          sx={{ padding: "1em 1.2em", mb: 2 }}
        >
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, mb: 0.5 }}>
            Not on a care home register
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#54596B", mb: 1.2 }}>
            {canAddToARegister
              ? "This person is known to eMHA but is not recorded as resident at any of the homes you can see. Adding them starts their capacity record."
              : "This person is known to eMHA but is not recorded as resident at any of the homes you can see. You do not have authority to change a register, so somebody at the home will need to add them."}
          </Typography>
          {canAddToARegister && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Select
                size="small"
                displayEmpty
                value={careHomeToAdd}
                onChange={(event) => {
                  setCareHomeToAdd(event.target.value);
                }}
                sx={{ minWidth: "16rem" }}
              >
                <MenuItem value="" disabled>
                  Select a care home...
                </MenuItem>
                {addableHomes.map((home) => (
                  <MenuItem key={home.id} value={home.id}>
                    {home.name}
                  </MenuItem>
                ))}
              </Select>
              <Button
                label="Add to register"
                disabled={!careHomeToAdd || isAdding}
                onClick={async () => {
                  setIsAdding(true);
                  try {
                    const response = await api.capacityState.addResident({
                      patientId: patientId!,
                      careHomeId: careHomeToAdd,
                      residencyStartDateTime: dayjs().toISOString(),
                    });

                    if (response.status >= 400) {
                      renderErrorToast({
                        message:
                          response.status === 400
                            ? "This person is already on a register. Record a transfer instead."
                            : "Could not add this person to the register",
                      });
                      return;
                    }

                    renderSuccessToast({ message: "Resident added" });
                    await Promise.all([refetchHistory(), refetchTimeline()]);
                  } finally {
                    setIsAdding(false);
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      <Typography
        sx={{
          fontSize: "0.68rem",
          letterSpacing: "0.1em",
          fontWeight: 700,
          color: "#54596B",
          mb: 0.8,
        }}
      >
        ASSESSMENT PATHWAY
      </Typography>
      <Paper variant="outlined" sx={{ mb: 2 }}>
        {pathway.map((step, index) => {
          const formState = pathwayFormState(
            step.templateId,
            assessmentForms,
            invitedTemplateId,
          );

          return (
            <Box
              key={step.key}
              data-testid={`pathway-${step.key}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                padding: "0.9em 1.1em",
                borderBottom:
                  index === pathway.length - 1
                    ? "none"
                    : `1px solid ${theme.palette.background.horizontalGray}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: "3px",
                    height: "30px",
                    borderRadius: "2px",
                    backgroundColor: step.stripe,
                  }}
                />
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  {step.title}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {formState.kind === "not-required" && (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#7B7D89",
                      fontStyle: "italic",
                    }}
                  >
                    Not required — has capacity for this decision
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    label="Record"
                    variant="outlined"
                    size="small"
                    disabled={!canManageThisResident}
                    startIcon={ButtonIcon.create}
                    onClick={() => {
                      setShowRecord(true);
                    }}
                  />
                  {formState.kind === "in-progress" && (
                    <Button
                      label="Continue"
                      size="small"
                      startIcon={ButtonIcon.arrow}
                      onClick={() => {
                        if (residenceAssessment)
                          navigate(
                            routeFns.formContextPage(
                              residenceAssessment.id,
                              patientId!,
                            ),
                          );
                      }}
                    />
                  )}
                  <Button
                    // Capacity is decision- and time-specific, so a completed assessment
                    // never closes the door on the next one. An owed BID is named for
                    // what it is, so the row reads as an obligation rather than a slot.
                    label={
                      formState.kind === "invited"
                        ? "Add corresponding BID"
                        : isRenewal
                          ? "Renew"
                          : "Form"
                    }
                    variant={
                      formState.kind === "invited" ? "contained" : "outlined"
                    }
                    size="small"
                    // The deprivation of liberty test has no form template yet, so its
                    // outcome can only be recorded by hand.
                    disabled={!step.templateId}
                    startIcon={ButtonIcon.ie}
                    onClick={() => {
                      if (step.templateId) setFormTemplateId(step.templateId);
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Paper>

      <Paper variant="outlined" sx={{ padding: "1.1em 1.2em" }}>
        <Typography
          sx={{
            fontSize: "0.68rem",
            letterSpacing: "0.1em",
            fontWeight: 700,
            color: "#54596B",
            mb: 1.2,
          }}
        >
          ASSESSMENT HISTORY
        </Typography>

        {inProgressForms.map((form) => (
          <Box
            key={form.id}
            data-testid="history-entry-in-progress"
            sx={{
              borderLeft: "3px dashed #9A9CA1",
              paddingLeft: "0.9em",
              marginBottom: "1.2em",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                {form.formTemplate.id === FDMCA_TEMPLATE_ID
                  ? "Mental Capacity Assessment"
                  : "Best Interest Decision"}
              </Typography>
              <Box
                component="span"
                sx={{
                  backgroundColor: "#FBF2D6",
                  color: "#7A5E00",
                  fontSize: "0.6rem",
                  letterSpacing: "0.08em",
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: "3px",
                }}
              >
                IN PROGRESS
              </Box>
            </Box>
            <Typography sx={{ fontSize: "0.8rem", color: "#7B7D89" }}>
              Started but not yet signed, so it has not changed the status.
            </Typography>
          </Box>
        ))}

        {!history?.length && inProgressForms.length === 0 ? (
          <Banner
            bannerType={BannerList.INFO}
            title="Nothing has been recorded for this resident yet"
          />
        ) : (
          history?.map((state) => (
            <HistoryEntry
              key={state.id}
              state={state}
              patientId={patientId!}
              onViewPdf={setPdfViewFormId}
            />
          ))
        )}
      </Paper>

      {showRecord && current && (
        <RecordCapacityStatusDialog
          row={{
            rootPatientId: patientId!,
            patientInfo: {
              name: patient?.name ?? { given: "", family: "" },
              dateOfBirth: patient?.dateOfBirth ?? null,
              nhsNumber: patient?.nhsNumber ?? null,
              restricted: false,
              deathNotificationStatus: null,
            },
            capacityState: current,
            careHomeName: careHomeName ?? "",
          }}
          onClose={() => {
            setShowRecord(false);
          }}
          onSubmit={async (args) => {
            const now = dayjs().toISOString();
            const response = await api.capacityState.recordStatus(patientId!, {
              effectiveDateTime: now,
              capacity: { outcome: args.capacityOutcome, validFrom: now },
              deprivation: { outcome: args.deprivationOutcome, validFrom: now },
              dols: args.dolsStatus
                ? {
                    status: args.dolsStatus,
                    startDateTime: now,
                    expiryDateTime: args.reviewDate
                      ? dayjs(args.reviewDate).endOf("day").toISOString()
                      : undefined,
                  }
                : undefined,
              reason: args.reason,
            });

            if (response.status < 400) {
              setShowRecord(false);
              await Promise.all([refetchHistory(), refetchTimeline()]);
            }
          }}
        />
      )}

      {pdfViewFormId && (
        <FormContextPdfViewer
          inModal
          // The whole assessment is offered, not just the form the entry names: a BID is
          // read alongside the capacity assessment it follows from.
          forms={
            [
              ...(timeline?.inProgressWork?.items ?? []),
              ...(timeline?.completedWork?.items ?? []),
            ].find((context) =>
              context.forms.some((form) => form.id === pdfViewFormId),
            )?.forms ?? []
          }
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}

      {formTemplateId && (
        <NewFormDialog
          patientId={patientId!}
          title="Start assessment"
          allowRequestForm={false}
          // Only the MCA templates are offered: this page is the whole of an MCA team's
          // patient record, and the rest of the form library is not theirs to launch.
          formTemplateFilterFn={(template) => template.id === formTemplateId}
          closeDialog={() => {
            setFormTemplateId(null);
          }}
        />
      )}

      {showRemove && (
        <RemoveResidentDialog
          patientId={patientId!}
          residentName={
            patient
              ? `${patient.name.given} ${patient.name.family}`
              : "resident"
          }
          careHomeName={careHomeName}
          onClose={() => {
            setShowRemove(false);
          }}
          onRemoved={() => {
            navigate(routeFns.careHomeRegister());
          }}
        />
      )}
    </Box>
  );
}
